import { useMemo } from 'react';

import TimelineClientMessage from 'src/features/product/product-detail/ui/timeline/TimelineClientMessage';
import { EntityTimeline, ExpectedTag } from 'src/shared/components';
import { BulkOrdering } from 'src/shared/types/bulkOrdering';

import LabelValueColumn from 'src/shared/components/LabelValueColumn';
import { Typography } from 'src/shared/components/ui';

import {
  CAVELA_DIRECT_STAGE_ICONS,
  CAVELA_DIRECT_STAGE_LABELS,
  CAVELA_DIRECT_STAGE_TO_GROUP_MAP,
  STAGE_ICONS,
  STAGE_LABELS,
  STAGE_TO_GROUP_MAP
} from '../constants/bulkOrderingTimeline';
import getDeliveredDate from '../utils/getDeliveredDate';

interface OrderModalTimelineProps {
  order: BulkOrdering | null;
  isOpen: boolean;
}

const OrderModalTimeline: React.FC<OrderModalTimelineProps> = ({
  order,
  isOpen
}) => {
  const isCavelaDirectOrder = order?.is_cavela_direct ?? false;
  const stageIcons = isCavelaDirectOrder
    ? CAVELA_DIRECT_STAGE_ICONS
    : STAGE_ICONS;
  const stageLabels = isCavelaDirectOrder
    ? CAVELA_DIRECT_STAGE_LABELS
    : STAGE_LABELS;
  const stageToGroupMap = isCavelaDirectOrder
    ? CAVELA_DIRECT_STAGE_TO_GROUP_MAP
    : STAGE_TO_GROUP_MAP;

  const currentStage = useMemo(() => {
    if (!order?.timeline) return null;
    const lastIndex = order?.timeline.findLastIndex(
      (value) => value.stage === order?.stage
    );

    if (lastIndex === -1) return null;

    return {
      ...order?.timeline[lastIndex],
      timelineIndex: lastIndex
    };
  }, [order?.timeline, order?.stage]);

  return (
    <div className="flex flex-col">
      <div className="px-5 pb-7 border-b border-gray-4 flex flex-col gap-5">
        <Typography variant="h5" weight="bold">
          Overview
        </Typography>

        <div className="grid grid-cols-2 gap-5">
          <LabelValueColumn
            label="Current stage"
            value={stageLabels[order?.stage || ''] || order?.stage}
            valueVariant="h4"
            valueWeight="bold"
          />
          <LabelValueColumn
            label="Order in hand"
            value={
              order?.estimated_end_date ? (
                <div className="flex items-center gap-1">
                  <Typography variant="h4" weight="bold">
                    {getDeliveredDate(order)}
                  </Typography>
                  {!(
                    order?.stage?.includes('rejected') ||
                    order?.stage?.includes('delivered')
                  ) ? (
                    <ExpectedTag />
                  ) : null}
                </div>
              ) : null
            }
            valueVariant="h4"
            valueWeight="bold"
          />
        </div>

        {currentStage?.client_notes && (
          <TimelineClientMessage stage={currentStage} />
        )}
      </div>

      <div className="px-5 py-7 border-b border-gray-4 flex flex-col gap-5">
        <Typography variant="h5" weight="bold">
          Progress
        </Typography>

        {order?.timeline && currentStage && (
          <EntityTimeline
            timeline={order?.timeline}
            currentStage={currentStage}
            isOpen={isOpen}
            stageIcons={stageIcons}
            stageLabels={stageLabels}
            stageToGroupMap={stageToGroupMap}
          />
        )}
      </div>
    </div>
  );
};

export default OrderModalTimeline;
